import { getDefinition } from "./catalog";
import { busbarStudList } from "./effective";
import { isJunction } from "./node-kind";
import { findNode } from "./system-lookup";
import type { Cable, System, SystemNode } from "./types";

/**
 * `promoteIds` / `studGrowth` are for tests and debugging — `nodes` already has
 * both applied.
 */
export type CableAdditionPlan = {
  cables: Cable[];
  nodes: SystemNode[];
  /** Provisional junctions that now have ≥2 cables and become real. */
  promoteIds: Set<string>;
  /** Busbar node-id → grown visible stud count; only entries that grew. */
  studGrowth: Map<string, number>;
};

/**
 * Pure planner for attaching a new cable. Busbars grow so visible studs match
 * cable count, counting **stud-side** cables only — the input feed sits on the
 * opposite face, and counting it leaves an empty stud at the rod end. The
 * studs remain available after a cable is removed, so spare connection points
 * and their identities survive editing.
 *
 * The caller generates the cable id and runs the terminal recompute + tidy.
 */
export function planCableAddition(newCable: Cable, system: System): CableAdditionPlan {
  const cables = [...system.cables, newCable];

  const promoteIds = new Set<string>();
  for (const endpointId of [newCable.fromNodeId, newCable.toNodeId]) {
    const node = findNode(system, endpointId);
    if (node?.kind === "component" && isJunction(node) && node.provisional) {
      const count = cables.reduce(
        (acc, c) =>
          c.fromNodeId === endpointId || c.toNodeId === endpointId ? acc + 1 : acc,
        0,
      );
      if (count >= 2) promoteIds.add(endpointId);
    }
  }

  const studGrowth = new Map<string, number>();
  for (const endpointId of [newCable.fromNodeId, newCable.toNodeId]) {
    const node = findNode(system, endpointId);
    if (!node || node.kind !== "component") continue;
    const def = getDefinition(node.definitionId, node.customDefinition);
    if (!def || def.renderVariant !== "busbar") continue;
    const studAxis = def.height >= def.width ? "right" : "bottom";
    const studsOnRight = def.terminals.filter((t) => t.side === studAxis).length;
    const studTermIds = new Set(busbarStudList(def, node.studCount).map((t) => t.id));
    const cableCount = cables.reduce((acc, c) => {
      if (c.fromNodeId === endpointId && studTermIds.has(c.fromTerminalId)) return acc + 1;
      if (c.toNodeId === endpointId && studTermIds.has(c.toTerminalId)) return acc + 1;
      return acc;
    }, 0);
    const need = Math.max(1, cableCount);
    const current = node.studCount ?? studsOnRight;
    if (need > current) studGrowth.set(endpointId, need);
  }

  const baseNodes =
    promoteIds.size === 0
      ? system.nodes
      : system.nodes.map((n) =>
          n.kind === "component" && isJunction(n) && promoteIds.has(n.id)
            ? ({ ...n, provisional: false } as SystemNode)
            : n,
        );
  const nodes =
    studGrowth.size === 0
      ? baseNodes
      : baseNodes.map((n) =>
          n.kind === "component" && studGrowth.has(n.id)
            ? ({ ...n, studCount: studGrowth.get(n.id)! } as SystemNode)
            : n,
        );

  return { cables, nodes, promoteIds, studGrowth };
}
